import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, X, Star, Check, Loader2, Globe } from 'lucide-react';
import { TokenConfig, POPULAR_MONAD_TOKENS } from '../../lib/WebAgentEngine';
import { isAddress } from 'viem';
import { getTokenImageUrl, fetchTokenInfo } from '../../lib/tokenImages';

interface TokenSelectorProps {
    selectedToken: TokenConfig | null;
    onSelectToken: (token: TokenConfig) => void;
}

const TokenSelector: React.FC<TokenSelectorProps> = ({ selectedToken, onSelectToken }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customAddress, setCustomAddress] = useState('');
    const [customSymbol, setCustomSymbol] = useState('');
    const [error, setError] = useState('');
    const [savedTokens, setSavedTokens] = useState<TokenConfig[]>([]);
    const [isFetching, setIsFetching] = useState(false);
    const [fetchedInfo, setFetchedInfo] = useState<{ symbol: string; name: string; imageUrl: string; priceUsd: number } | null>(null);

    // Load saved tokens from localStorage
    useEffect(() => {
        const saved = localStorage.getItem('duckmon_saved_tokens');
        if (saved) {
            try {
                setSavedTokens(JSON.parse(saved));
            } catch {
                // Ignore parse errors
            }
        }
    }, []);

    // Combine popular + saved tokens
    const allTokens = [...POPULAR_MONAD_TOKENS, ...savedTokens];

    // Auto-fetch token metadata when a valid address is entered
    const autoFetchMetadata = useCallback(async (address: string) => {
        if (!address.startsWith('0x') || address.length !== 42 || !isAddress(address)) {
            setFetchedInfo(null);
            return;
        }

        // Check if already known
        const exists = allTokens.some(t => t.address.toLowerCase() === address.toLowerCase());
        if (exists) {
            setFetchedInfo(null);
            setError('Token already in your list');
            return;
        }

        setIsFetching(true);
        setError('');
        setFetchedInfo(null);

        const info = await fetchTokenInfo(address);

        if (info) {
            setFetchedInfo({
                symbol: info.symbol,
                name: `${info.symbol}`,
                imageUrl: info.imageUrl,
                priceUsd: info.priceUsd,
            });
            setCustomSymbol(info.symbol);
        } else {
            setFetchedInfo(null);
            // Don't show error - user can still manually enter symbol
        }
        setIsFetching(false);
    }, [allTokens]);

    // Debounced address change handler
    useEffect(() => {
        if (customAddress.length === 42) {
            const timer = setTimeout(() => autoFetchMetadata(customAddress), 300);
            return () => clearTimeout(timer);
        } else {
            setFetchedInfo(null);
        }
    }, [customAddress, autoFetchMetadata]);

    const handleAddCustomToken = () => {
        setError('');

        if (!customAddress.startsWith('0x') || customAddress.length !== 42) {
            setError('Invalid address format');
            return;
        }

        if (!isAddress(customAddress)) {
            setError('Invalid Ethereum address');
            return;
        }

        const symbol = customSymbol || fetchedInfo?.symbol;
        if (!symbol || symbol.length < 1) {
            setError('Please enter a symbol');
            return;
        }

        const newToken: TokenConfig = {
            address: customAddress as `0x${string}`,
            symbol: symbol.toUpperCase(),
            name: fetchedInfo?.name || `${symbol.toUpperCase()}`
        };

        // Check if already exists
        const exists = allTokens.some(t => t.address.toLowerCase() === newToken.address.toLowerCase());
        if (exists) {
            setError('Token already exists');
            return;
        }

        // Save to localStorage
        const updated = [...savedTokens, newToken];
        setSavedTokens(updated);
        localStorage.setItem('duckmon_saved_tokens', JSON.stringify(updated));

        // Select the new token
        onSelectToken(newToken);
        setCustomAddress('');
        setCustomSymbol('');
        setFetchedInfo(null);
        setIsOpen(false);
    };

    const removeCustomToken = (address: string) => {
        const updated = savedTokens.filter(t => t.address.toLowerCase() !== address.toLowerCase());
        setSavedTokens(updated);
        localStorage.setItem('duckmon_saved_tokens', JSON.stringify(updated));
    };

    const TokenImage: React.FC<{ symbol: string; address: string; size?: number }> = ({ symbol, address, size = 24 }) => {
        const [imgError, setImgError] = useState(false);
        const src = getTokenImageUrl(symbol, address);

        if (imgError || !src) {
            // Gradient fallback
            return (
                <div
                    className="token-img-fallback"
                    style={{
                        width: size, height: size, borderRadius: '50%',
                        background: `linear-gradient(135deg, hsl(${symbol.charCodeAt(0) * 7 % 360}, 70%, 50%), hsl(${symbol.charCodeAt(0) * 7 % 360 + 60}, 70%, 40%))`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: size * 0.45, fontWeight: 700, color: '#fff',
                    }}
                >
                    {symbol.slice(0, 2)}
                </div>
            );
        }

        return (
            <img
                src={src}
                alt={symbol}
                width={size}
                height={size}
                style={{ borderRadius: '50%', objectFit: 'cover' }}
                onError={() => setImgError(true)}
            />
        );
    };

    return (
        <div className="token-selector">
            {/* Selected Token Display */}
            <button
                className="selected-token-btn"
                onClick={() => setIsOpen(!isOpen)}
            >
                {selectedToken ? (
                    <>
                        <TokenImage symbol={selectedToken.symbol} address={selectedToken.address} size={20} />
                        <span className="token-symbol">{selectedToken.symbol}</span>
                        <span className="token-address">
                            {selectedToken.address.slice(0, 6)}...{selectedToken.address.slice(-4)}
                        </span>
                    </>
                ) : (
                    <span className="placeholder">Select Token</span>
                )}
                <Search size={16} />
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="token-dropdown">
                    <div className="dropdown-header">
                        <h4>Select Token to Analyze</h4>
                        <button className="close-btn" onClick={() => setIsOpen(false)}>
                            <X size={16} />
                        </button>
                    </div>

                    {/* Popular Tokens */}
                    <div className="token-section">
                        <h5>
                            <Star size={14} />
                            Popular Tokens
                        </h5>
                        <div className="token-list">
                            {POPULAR_MONAD_TOKENS.map(token => (
                                <button
                                    key={token.address}
                                    className={`token-option ${selectedToken?.address === token.address ? 'selected' : ''}`}
                                    onClick={() => {
                                        onSelectToken(token);
                                        setIsOpen(false);
                                    }}
                                >
                                    <TokenImage symbol={token.symbol} address={token.address} size={24} />
                                    <div className="token-info">
                                        <span className="symbol">{token.symbol}</span>
                                        <span className="name">{token.name}</span>
                                    </div>
                                    {selectedToken?.address === token.address && <Check size={16} />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Saved Custom Tokens */}
                    {savedTokens.length > 0 && (
                        <div className="token-section">
                            <h5>Your Tokens</h5>
                            <div className="token-list">
                                {savedTokens.map(token => (
                                    <button
                                        key={token.address}
                                        className={`token-option ${selectedToken?.address === token.address ? 'selected' : ''}`}
                                        onClick={() => {
                                            onSelectToken(token);
                                            setIsOpen(false);
                                        }}
                                    >
                                        <TokenImage symbol={token.symbol} address={token.address} size={24} />
                                        <div className="token-info">
                                            <span className="symbol">{token.symbol}</span>
                                            <span className="address">
                                                {token.address.slice(0, 8)}...{token.address.slice(-6)}
                                            </span>
                                        </div>
                                        <button
                                            className="remove-token-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeCustomToken(token.address);
                                            }}
                                        >
                                            <X size={14} />
                                        </button>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add Custom Token */}
                    <div className="token-section custom-section">
                        <h5>
                            <Plus size={14} />
                            Add Any Token
                        </h5>
                        <div className="custom-token-form">
                            <div className="address-input-wrapper">
                                <Globe size={14} className="input-icon" />
                                <input
                                    type="text"
                                    placeholder="Paste token address (0x...)"
                                    value={customAddress}
                                    onChange={(e) => setCustomAddress(e.target.value.trim())}
                                    className="address-input"
                                />
                                {isFetching && <Loader2 size={14} className="spinner input-spinner" />}
                            </div>

                            {/* Auto-fetched info preview */}
                            {fetchedInfo && (
                                <div className="fetched-preview">
                                    <TokenImage symbol={fetchedInfo.symbol} address={customAddress} size={28} />
                                    <div className="fetched-details">
                                        <span className="fetched-symbol">{fetchedInfo.symbol}</span>
                                        {fetchedInfo.priceUsd > 0 && (
                                            <span className="fetched-price">${fetchedInfo.priceUsd < 0.01 ? fetchedInfo.priceUsd.toExponential(2) : fetchedInfo.priceUsd.toFixed(4)}</span>
                                        )}
                                    </div>
                                    <Check size={14} style={{ color: '#4ade80' }} />
                                </div>
                            )}

                            {!fetchedInfo && (
                                <input
                                    type="text"
                                    placeholder="Symbol (auto-detected or enter manually)"
                                    value={customSymbol}
                                    onChange={(e) => setCustomSymbol(e.target.value.slice(0, 10))}
                                    maxLength={10}
                                    className="symbol-input"
                                />
                            )}

                            <button
                                className="add-token-btn"
                                onClick={handleAddCustomToken}
                                disabled={isFetching}
                            >
                                {isFetching ? <Loader2 size={14} className="spinner" /> : 'Add Token'}
                            </button>
                        </div>
                        {error && <p className="error-message">{error}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TokenSelector;
